import { observer } from 'mobx-react-lite';
import { useResolve } from '../use';
import { TOKEN_CoachStoreFor, type CoachRow, type CoachStore } from './CoachStore';
import type { Court } from '@/domain/session';

// One screen, no navigation: the court's player numbers, ordered by number, each row with an
// upvote, a downvote, a big button and a lightbulb (docs/scoring.md, "The coach interface").
//
// The user is a coach mid-drill with a whistle in the other hand, glancing down for less than a
// second. Everything on the screen is sized for that: the player number is the biggest thing on
// its row because it is what the coach matches against a bib, the buttons are square and
// finger-sized rather than icon-sized, and nothing moves position as votes land.
//
// Legible beats pretty, deliberately. The goal puts anything visual beyond legible out of scope.

const COURT_TONE: Record<Court, string> = {
  white: 'bg-neutral-200 text-neutral-900',
  red: 'bg-rose-600 text-white',
  purple: 'bg-purple-600 text-white',
  gold: 'bg-amber-500 text-neutral-900',
};

// The court comes in as a prop because it is the shell that knows what this device redeemed
// (docs/access.md). The store is keyed on it, so the screen never has to hold one.
export const CoachView = observer(function CoachView({ court }: { court: Court }) {
  const coach = useResolve(TOKEN_CoachStoreFor)(court);

  return (
    <div className="flex flex-1 flex-col gap-3 p-3">
      <header className="flex items-baseline justify-between">
        <span
          className={`rounded-lg px-3 py-1 text-lg font-bold uppercase ${COURT_TONE[court]}`}
          // The one fact a coach has to be able to check at a glance: this is the court their code
          // put them on, and there is no picker to change it.
        >
          {court} court
        </span>
        <span className="text-sm text-neutral-500">{coach.rows.length} on court</span>
      </header>

      {/* Why the buttons are dead. Rendered above the list rather than beside it: a coach who
          cannot vote needs to know before they have tapped four times. */}
      {coach.notice !== undefined && (
        <p
          role="status"
          className="rounded-lg bg-neutral-800 p-3 text-center text-lg text-neutral-200 ring-1 ring-neutral-700"
        >
          {coach.notice}
        </p>
      )}
      {/* A failed vote must be visibly failed (docs/sync.md): coach votes are the only signal the
          product has, and a silently dropped one is worse than a dropped one. */}
      {coach.failedMessage !== undefined && (
        <p
          role="alert"
          className="rounded-lg bg-amber-950 p-3 text-center text-lg text-amber-100 ring-1 ring-amber-800"
        >
          {coach.failedMessage}
        </p>
      )}

      {coach.rows.length === 0 ? (
        <p className="p-8 text-center text-neutral-500">
          {/* Neutral about WHY the court is empty, deliberately. Naming the front desk is right on
              the day's first screen and wrong at minute forty, when the court is empty because the
              mover walked the last player off it and the next tick will send someone back. */}
          Nobody is on this court. Rows appear as players are registered or moved onto it.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {coach.rows.map((row) => (
            <Row key={row.number} row={row} coach={coach} />
          ))}
        </ul>
      )}
    </div>
  );
});

// One player. The number and the score on the left, four thumb-sized targets on the right.
const Row = observer(function Row({ row, coach }: { row: CoachRow; coach: CoachStore }) {
  const disabled = !coach.canVote;

  return (
    <li className="flex items-center gap-2 rounded-xl bg-neutral-900 p-2 ring-1 ring-neutral-800">
      <div className="flex w-24 shrink-0 flex-col items-center">
        {/* The number is what a coach matches against a bib across a court. Nothing else on the
            row competes with it. */}
        <span className="text-[5rem] leading-none font-bold tabular-nums">{row.number}</span>
        <span className="text-lg text-neutral-400 tabular-nums">{row.score}</span>
      </div>
      <div className="grid flex-1 grid-cols-4 gap-2">
        <Tap
          label={`upvote player ${row.number}`}
          face="+"
          disabled={disabled}
          busy={coach.isPending(row.number, 'up')}
          tone="bg-emerald-700 text-white"
          onTap={() => void coach.upvote(row.number)}
        />
        <Tap
          label={`downvote player ${row.number}`}
          face="−"
          disabled={disabled}
          busy={coach.isPending(row.number, 'down')}
          tone="bg-rose-800 text-white"
          onTap={() => void coach.downvote(row.number)}
        />
        {/* The two flags are toggles that show their own state: a coach has to be able to read
            whether player 7 is already marked big without tapping to find out. */}
        <Tap
          label={`big, player ${row.number}`}
          face="BIG"
          pressed={row.big}
          disabled={disabled}
          busy={coach.isPending(row.number, 'big')}
          tone={row.big ? 'bg-sky-500 text-neutral-900' : 'bg-neutral-800 text-neutral-400'}
          onTap={() => void coach.toggleBig(row.number)}
        />
        <Tap
          label={`lightbulb, player ${row.number}`}
          face="IQ"
          pressed={row.lightbulb}
          disabled={disabled}
          busy={coach.isPending(row.number, 'lightbulb')}
          tone={row.lightbulb ? 'bg-amber-400 text-neutral-900' : 'bg-neutral-800 text-neutral-400'}
          onTap={() => void coach.toggleLightbulb(row.number)}
        />
      </div>
    </li>
  );
});

// A square target, big enough for a thumb on a phone held one-handed. `aria-pressed` is what
// carries a toggle's state to a screen reader and to an e2e query; the colour carries it to a
// coach.
//
// `busy` and `disabled` both stop a tap, and they must NOT look the same. Gated is dead: voting is
// closed and the notice above says why. Busy is working: this tap is in the air and the coach
// should wait rather than tap again. A busy button that faded to the gated 25% would read as "that
// did not register", which is exactly the misread that makes a coach smash it.
function Tap({
  label,
  face,
  tone,
  disabled,
  busy = false,
  pressed,
  onTap,
}: {
  label: string;
  face: string;
  tone: string;
  disabled: boolean;
  busy?: boolean;
  pressed?: boolean;
  onTap: () => void;
}) {
  return (
    <button
      aria-label={label}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      aria-busy={busy}
      disabled={disabled || busy}
      onClick={onTap}
      // Keeps its colour and its face while it works, so the coach can still see WHICH button they
      // hit. The pulse and the ring carry "in progress"; only the gated case dims to near-nothing.
      className={`h-16 rounded-lg text-2xl font-bold ${tone} ${
        busy ? 'animate-pulse opacity-70 ring-2 ring-white/60' : 'disabled:opacity-25'
      }`}
    >
      {face}
    </button>
  );
}
